import { z } from 'zod';

const multilingualContentSchema = z.object({
  english: z
    .string()
    .min(1, 'English content is required')
    .max(50000, 'Content is too long'),
  hindi: z.string().max(50000, 'Content is too long').optional(),
  marwari: z.string().max(50000, 'Content is too long').optional(),
});

const multilingualTitleSchema = z.object({
  english: z
    .string()
    .min(1, 'English title is required')
    .max(200, 'Title cannot exceed 200 characters'),
  hindi: z.string().max(200, 'Title cannot exceed 200 characters').optional(),
  marwari: z.string().max(200, 'Title cannot exceed 200 characters').optional(),
});

const multilingualShortDescSchema = z.object({
  english: z
    .string()
    .min(1, 'English short description is required')
    .max(500, 'Short description cannot exceed 500 characters'),
  hindi: z.string().max(500, 'Short description cannot exceed 500 characters').optional(),
  marwari: z.string().max(500, 'Short description cannot exceed 500 characters').optional(),
});

const seoSchema = z.object({
  metaTitle: z.string().max(60, 'Meta title should not exceed 60 characters').optional(),
  metaDescription: z.string().max(160, 'Meta description should not exceed 160 characters').optional(),
  metaKeywords: z.array(z.string()).optional(),
  ogImage: z.string().url('Invalid URL').optional().or(z.literal('')),
  ogTitle: z.string().optional(),
  ogDescription: z.string().optional(),
});

const contactInfoSchema = z.object({
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  mobile: z.string().optional(),
  address: z.string().optional(),
  grievanceOfficer: z
    .object({
      name: z.string().optional(),
      email: z.string().email('Invalid email').optional().or(z.literal('')),
      responseTime: z.string().optional(),
    })
    .optional(),
});

export const addCmsSchema = z.object({
  slug: z
    .string()
    .max(100, 'Slug cannot exceed 100 characters')
    .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers and hyphens')
    .optional()
    .or(z.literal('')),
  title: multilingualTitleSchema,
  pageType: z.enum([
    'terms_and_conditions',
    'privacy_policy',
    'about_us',
    'faq',
    'help_center',
    'refund_policy',
    'cancellation_policy',
    'shipping_policy',
    'contact_us',
    'trust_and_safety',
    'delete_account',
    'custom',
    'terms_and_conditions_customer',
    'terms_and_conditions_provider',
    'service_provider_agreement',
    'other_compliance',
    'disclaimer',
    'note'
  ], {
    required_error: 'Page type is required',
  }),
  content: multilingualContentSchema,
  shortDescription: multilingualShortDescSchema,
  seo: seoSchema.optional(),
  contactInfo: contactInfoSchema.optional(),
  lastUpdatedDate: z.string().optional(),
  effectiveDate: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived']).optional(),
  displayOrder: z.number().optional(),
});

export const updateCmsSchema = z.object({
  title: multilingualTitleSchema.partial().optional(),
  content: multilingualContentSchema.partial().optional(),
  shortDescription: multilingualShortDescSchema.partial().optional(),
  seo: seoSchema.optional(),
  contactInfo: contactInfoSchema.optional(),
  lastUpdatedDate: z.string().optional(),
  effectiveDate: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived']).optional(),
  displayOrder: z.number().optional(),
  changeNote: z.string().max(500, 'Change note cannot exceed 500 characters').optional(),
  isPublished: z.boolean().optional(),
});
